import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  tokens,
  Spinner,
  Input,
  Checkbox,
  Text,
  ProgressBar,
} from '@fluentui/react-components';
import { Warning24Regular, Dismiss24Regular, Checkmark24Regular, CheckmarkCircle24Regular } from '@fluentui/react-icons';
import type { Severity, MaintenanceJobStatus } from '../../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '480px',
    width: '90vw',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  iconWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  iconDestructive: {
    color: tokens.colorPaletteRedForeground1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  description: {
    color: tokens.colorNeutralForeground2,
  },
  affectedItems: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '4px',
    maxHeight: '120px',
    overflowY: 'auto',
  },
  affectedList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inputLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  progressSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
  },
  progressHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressPhase: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  progressPercent: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  progressMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (text: string) => Promise<void>;
  title: string;
  description: string;
  severity: Severity;
  affectedItems: string[];
  jobStatus?: MaintenanceJobStatus | null;
}

const phaseLabels: Record<string, string> = {
  'starting': 'Iniciando...',
  'deleting-tables': 'Deletando tabelas...',
  'triggering-backfill': 'Disparando recalculo...',
  'waiting-completion': 'Aguardando conclusao...',
  'done': 'Concluido!',
  'error': 'Erro',
};

export const ConfirmationModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  severity,
  affectedItems,
  jobStatus,
}) => {
  const styles = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInputValue('');
      setUnderstood(false);
      setError(null);
    }
  }, [open]);

  const isPolling = !!(jobStatus && (jobStatus.status === 'pending' || jobStatus.status === 'running'));
  const isCompleted = jobStatus?.status === 'completed' || false;
  const canConfirm = inputValue === 'CONFIRMAR' && understood && !loading && !isPolling;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setError(null);
    try {
      await onConfirm(inputValue);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  const iconClass = severity === 'destructive' ? styles.iconDestructive : styles.iconWarning;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            <div className={styles.header}>
              <Warning24Regular className={iconClass} />
              {title}
            </div>
          </DialogTitle>

          <DialogContent>
            <div className={styles.content}>
              {/* Modo progresso - quando job está em execução */}
              {jobStatus && (isPolling || isCompleted) ? (
                <div className={styles.progressSection}>
                  <div className={styles.progressHeader}>
                    {isCompleted ? (
                      <CheckmarkCircle24Regular className={styles.successIcon} />
                    ) : (
                      <Spinner size="small" />
                    )}
                    <Text className={styles.progressPhase}>
                      {phaseLabels[jobStatus.phase] || jobStatus.phase}
                    </Text>
                  </div>
                  <ProgressBar value={jobStatus.progress / 100} />
                  <Text className={styles.progressPercent}>{jobStatus.progress}%</Text>
                  {jobStatus.message && (
                    <Text className={styles.progressMessage}>{jobStatus.message}</Text>
                  )}
                  {isCompleted && jobStatus.tablesDeleted.length > 0 && (
                    <Text className={styles.progressMessage}>
                      Tabelas processadas: {jobStatus.tablesDeleted.join(', ')}
                    </Text>
                  )}
                </div>
              ) : (
                /* Modo confirmação - formulário normal */
                <>
                  <Text className={styles.description}>{description}</Text>

                  {affectedItems.length > 0 && (
                    <div className={styles.affectedItems}>
                      <Text weight="semibold">Itens afetados:</Text>
                      <ul className={styles.affectedList}>
                        {affectedItems.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className={styles.inputSection}>
                    <Text className={styles.inputLabel}>
                      Digite <strong>CONFIRMAR</strong> para continuar:
                    </Text>
                    <Input
                      value={inputValue}
                      onChange={(_, data) => setInputValue(data.value)}
                      placeholder="CONFIRMAR"
                      disabled={loading}
                    />
                  </div>

                  <Checkbox
                    checked={understood}
                    onChange={(_, data) => setUnderstood(!!data.checked)}
                    label="Entendo que esta acao e irreversivel"
                    disabled={loading}
                  />
                </>
              )}

              {error && <Text className={styles.errorText}>{error}</Text>}
            </div>
          </DialogContent>

          <DialogActions>
            {isCompleted ? (
              <Button appearance="primary" icon={<Checkmark24Regular />} onClick={onClose}>
                Fechar
              </Button>
            ) : (
              <>
                <Button
                  appearance="secondary"
                  icon={<Dismiss24Regular />}
                  onClick={onClose}
                  disabled={loading || isPolling}
                >
                  Cancelar
                </Button>
                <Button
                  appearance="primary"
                  icon={loading ? <Spinner size="tiny" /> : <Checkmark24Regular />}
                  onClick={handleConfirm}
                  disabled={!canConfirm || isPolling}
                >
                  {loading ? 'Executando...' : isPolling ? 'Processando...' : 'Confirmar'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
